import { useState } from 'react';
import { useAuth, endpointRegistry } from '@locus-taxy/encore-ui-network';
import type { ApiError } from '@locus-taxy/encore-ui-network';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import { registerFileEndpoints } from './endpoints';

// Ensure endpoints are registered
registerFileEndpoints();

export interface DocumentInfo {
  fileId: string;
  fileName?: string;
  fileType?: string;
  contentType?: string;
  storageMetadata?: {
    url?: string;
    bucketName?: string;
    key?: string;
    provider?: 'S3' | 'EXTERNAL';
  };
}

interface UseFileUploadReturn {
  uploadFile: (file: File) => Promise<DocumentInfo>;
  uploading: boolean;
}

export function useFileUpload(clientId: string): UseFileUploadReturn {
  const [uploading, setUploading] = useState(false);
  const { apiClient } = useAuth();
  const { showApiError } = useToast();

  const uploadFile = async (file: File): Promise<DocumentInfo> => {
    setUploading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Get the endpoint with clientId parameter
      const endpoint = endpointRegistry.getEndpoint('files.upload', {
        clientId,
      });

      // Upload file using the API client
      const response = await apiClient.post<{
        data: DocumentInfo;
        success: boolean;
      }>(endpoint, formData, {
        requireAuth: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!response.success || !response.data) {
        throw new Error('File upload failed');
      }

      return response.data;
    } catch (err) {
      showApiError(err as ApiError);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadFile,
    uploading,
  };
}
